import React, { useState } from "react"
import { Div, Text } from "atomize"
import Card from "../Card"
import { theme } from "../settings/config"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUsers, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons"
import { formatAmount } from "../utils/billing.utils"
import { formatHoursAmount } from "../utils/week.utils"

const VISIBLE_BY_DEFAULT = 3

const PlayersCard = ({ playersLessonCount }) => {
	const [showAll, setShowAll] = useState(false)
	const [sortBy, setSortBy] = useState("hours")

	const entries = Object.entries(playersLessonCount ?? {}).filter(
		([, entry]) => entry.lessons > 0 || entry.absences > 0,
	)

	if (entries.length === 0) return null

	const hasAmounts = entries.some(([, entry]) => entry.amount > 0)

	// il pagato ha senso solo se qualcuno ha pagato: con le lezioni tutte
	// fatturate al circolo la colonna degli importi non c'è nemmeno
	const sortOptions = [
		{ key: "hours", label: "Ore", compare: (a, b) => b.hours - a.hours },
		{ key: "lessons", label: "Lezioni", compare: (a, b) => b.lessons - a.lessons },
		...(hasAmounts
			? [{ key: "amount", label: "Pagato", compare: (a, b) => b.amount - a.amount }]
			: []),
	]
	const activeSort = sortOptions.find(option => option.key === sortBy) ?? sortOptions[0]

	const players = [...entries].sort(
		([, a], [, b]) => activeSort.compare(a, b) || a.playerName.localeCompare(b.playerName),
	)
	const visible = showAll ? players : players.slice(0, VISIBLE_BY_DEFAULT)

	return (
		<Card bg={theme.colors.surface}>
			<Div d="flex" align="center" justify="space-between" m={{ b: "1rem" }}>
				<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
					<Div
						w="40px"
						h="40px"
						rounded="circle"
						bg={theme.colors.blue_light}
						d="flex"
						align="center"
						justify="center">
						<FontAwesomeIcon icon={faUsers} color={theme.colors.blue} />
					</Div>
					<Text textWeight="bold" textSize="title">
						Allievi
					</Text>
				</Div>
				<Div
					p="0.25rem 0.75rem"
					rounded="circle"
					bg={theme.colors.blue_light}
					d="flex"
					align="center">
					<Text textWeight="bold" textColor={theme.colors.blue} textSize="subheader">
						{players.length}
					</Text>
				</Div>
			</Div>

			<Div
				d="flex"
				align="center"
				style={{ gap: "0.5rem", flexWrap: "wrap" }}
				m={{ b: "0.75rem" }}>
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ r: "0.25rem" }}>
					Ordina per
				</Text>
				{sortOptions.map(option => (
					<Div
						key={option.key}
						p="0.25rem 0.75rem"
						rounded="circle"
						cursor="pointer"
						bg={
							activeSort.key === option.key
								? theme.colors.gunmetal_fill
								: theme.colors.gray_light
						}
						onClick={() => setSortBy(option.key)}>
						<Text
							textSize="caption"
							textWeight={activeSort.key === option.key && "bold"}
							textColor={activeSort.key === option.key ? "white" : "black"}>
							{option.label}
						</Text>
					</Div>
				))}
			</Div>

			{visible.map(([playerID, entry]) => (
				<Div
					key={playerID}
					d="flex"
					align="center"
					justify="space-between"
					p={{ y: "0.4rem" }}
					border="bottom"
					borderColor={theme.colors.gray_light}>
					<Div d="flex" flexDir="column">
						<Text textSize="subheader">{entry.playerName}</Text>
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{entry.lessons} {entry.lessons === 1 ? "lezione" : "lezioni"} ·{" "}
							{formatHoursAmount(entry.hours)}
							{entry.absences > 0 &&
								` · ${entry.absences} ${
									entry.absences === 1 ? "assenza" : "assenze"
								}`}
						</Text>
					</Div>
					{hasAmounts && (
						<Text textSize="subheader" textWeight="bold">
							{formatAmount(entry.amount)} €
						</Text>
					)}
				</Div>
			))}

			{players.length > VISIBLE_BY_DEFAULT && (
				<Div
					d="flex"
					align="center"
					justify="center"
					style={{ gap: "0.5rem", cursor: "pointer" }}
					p={{ t: "0.75rem" }}
					onClick={() => setShowAll(!showAll)}>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{showAll
							? "Mostra meno"
							: `Mostra tutti (${players.length - VISIBLE_BY_DEFAULT} in più)`}
					</Text>
					<FontAwesomeIcon
						icon={showAll ? faChevronUp : faChevronDown}
						color={theme.colors.text_light}
						size="xs"
					/>
				</Div>
			)}
		</Card>
	)
}

export default PlayersCard
